'use client';

import { useAppKit, useAppKitAccount, useAppKitNetwork, useAppKitProvider, useDisconnect } from '@reown/appkit/react';
import { ethers } from 'ethers';
import { createContext, useCallback, useContext } from 'react';

type AppKitModalProviderProps = {
  children: React.ReactNode;
};

type AppKitModalContent = {
  connectedWalletAddress?: string;
  isConnected: boolean;
  connectedChain: string | number | undefined;
  walletProvider: unknown;
  onConnectWallet: () => Promise<void>;
  onDisconnect: () => Promise<void>;
  getConnectedChainId: () => Promise<number>;
  onSignPersonalMessage: (message: string) => Promise<string | undefined>;
  onSwitchConfigNetwork: (chainID: string) => Promise<unknown>;
  onConnectWalletAddress: () => Promise<string | undefined>;
};

const AppKitModalContext = createContext<AppKitModalContent | undefined>(undefined);

export const AppKitModalProvider: React.FC<AppKitModalProviderProps> = ({ children }) => {
  const { open } = useAppKit();
  const { address: connectedWalletAddress, isConnected } = useAppKitAccount();
  const { chainId: connectedChain } = useAppKitNetwork();
  const { disconnect } = useDisconnect();
  const { walletProvider } = useAppKitProvider('eip155');

  // Connect wallet address
  const onConnectWalletAddress = useCallback(async () => {
    return connectedWalletAddress;
  }, [connectedWalletAddress]);

  // Disconnect wallet
  const onDisconnect = useCallback(async () => {
    if (!isConnected) return;
    try {
      await disconnect();
    } catch {
      throw new Error('Disconnection failed');
    }
  }, [disconnect, isConnected]);

  // Connect wallet
  const onConnectWallet = useCallback(async () => {
    // If is already connected, disconnect
    if (isConnected) {
      await onDisconnect();
    }
    try {
      await open({ view: 'Connect' });
    } catch {
      throw new Error('Connection failed');
    }
  }, [isConnected, onDisconnect, open]);

  // Sign a personal message
  const onSignPersonalMessage = useCallback(
    async (message: string) => {
      if (!isConnected || !connectedWalletAddress) {
        throw new Error('Please connect your wallet first.');
      }

      try {
        if (!walletProvider) {
          throw new Error('Wallet provider not available.');
        }
        const provider = new ethers.providers.Web3Provider(walletProvider);
        const signer = provider.getSigner();

        return await signer.signMessage(message);
      } catch {
        throw new Error('Sign failed. Please try again.');
      }
    },
    [connectedWalletAddress, isConnected, walletProvider]
  );

  // connectedChain
  const getConnectedChainId = useCallback(async () => {
    try {
      if (!walletProvider) {
        throw new Error('Wallet provider not available.');
      }
      const provider = new ethers.providers.Web3Provider(walletProvider);

      return await provider.getNetwork().then((n) => n.chainId);
    } catch (error) {
      throw error;
    }
  }, [walletProvider]);

  // Switch network
  const onSwitchConfigNetwork = useCallback(
    async (chainID: string) => {
      try {
        if (!walletProvider) {
          throw new Error('Wallet provider not available.');
        }
        const provider = new ethers.providers.Web3Provider(walletProvider);
        const hexValue = parseInt(chainID, 10).toString(16);
        const chain = hexValue.padStart(6, '');

        return await provider.send('wallet_switchEthereumChain', [{ chainId: `0x${chain}` }]);
      } catch {
        throw new Error('Unable to switch network. Please try again.');
      }
    },
    [walletProvider]
  );

  return (
    <AppKitModalContext.Provider
      value={{
        connectedWalletAddress,
        isConnected,
        connectedChain,
        onDisconnect,
        onConnectWalletAddress,
        getConnectedChainId,
        onSignPersonalMessage,
        onConnectWallet,
        onSwitchConfigNetwork,
        walletProvider
      }}
    >
      {children}
    </AppKitModalContext.Provider>
  );
};

// Custom hook to use the AppKitModalContent
export const useAppKitModalContent = (): AppKitModalContent => {
  const context = useContext(AppKitModalContext);

  if (!context) {
    throw new Error('useAppKitModalContent must be used within a AppKitModalProvider');
  }

  return context;
};
